import { Output, generateText } from 'ai';
import { z } from 'zod';

import { selectAdCopyTextModel } from '@/lib/ai-agent/workflow-model';
import {
  AD_COPY_ADDITIONAL_JUDGE_CHECKS,
  HOOK_QUALITY_CRITERIA,
  HOOK_SCORE_BANDS,
} from '@/lib/content-doctrine/ad-copy-doctrine';

/**
 * THE QUALITY REVIEW PASS — the difference between copy that is *valid* and
 * copy that is *good*.
 *
 * `checkAdContract` already answers "may this ship" (structure, evidence
 * safety, character limits). Nothing answered "is this any good", so copy that
 * was perfectly compliant and completely forgettable passed every gate and got
 * saved. This is that missing judge.
 *
 * WHY A SEPARATE MODEL CALL. A model is a poor critic of text it is in the
 * middle of composing — the same generation that produces a limp hook will
 * rate it acceptable, because the reasoning that produced it is still in
 * context. Handing the finished copy to a fresh call with an explicit rubric
 * and no authorship attachment gets a genuinely different answer. The rubric's
 * bands are anchored (`HOOK_SCORE_BANDS`) for the same reason: an unanchored
 * 1–10 ask makes a model answer 7 to nearly anything, which is worse than no
 * score at all because it looks like a gate while gating nothing.
 *
 * WHY IT ONLY EVER TRIGGERS ONE REWRITE. This is advisory quality, not safety.
 * A failed review returns notes and the caller may regenerate once; it must
 * never loop, because each pass is a paid model call and "keep going until the
 * critic is happy" is how a single ad-copy request becomes an unbounded spend.
 * The repo has shipped that bug before in another form. If the second attempt
 * still scores low, the copy is returned anyway with its score attached — an
 * honest mediocre draft the user can see and reject beats a silent retry storm.
 */

export const AdCopyReviewSchema = z.object({
  hookScore: z
    .number()
    .min(1)
    .max(10)
    .describe('Score for the strongest opening line, against the anchored bands.'),
  strongestLine: z.string().describe('The single line that is carrying the ad.'),
  weakestLine: z.string().describe('The single line most in need of a rewrite.'),
  isGeneric: z
    .boolean()
    .describe(
      'True if a direct competitor could run this copy unchanged with only the product name swapped.',
    ),
  failsBecause: z
    .array(z.string())
    .describe(
      'Concrete, actionable reasons the copy falls short, each naming the field and what to do instead. Empty when the copy is strong.',
    ),
  unsupportedAssertions: z
    .array(z.string())
    .describe(
      "Every sentence or clause in the copy that asserts a fact the EVIDENCE list does not state: how a tax, valuation, legal or regulatory mechanism works, what a rule or agency requires or causes, what happened, what state the reader's own assets or plans are in, a claim about how the advertiser works that the evidence does not make, or a product benefit, performance result, availability, shipping, price advantage, timing or audience the evidence does not state. Quote each one. Empty when every factual assertion is stated in the evidence.",
    ),
});
export type AdCopyReview = Omit<z.infer<typeof AdCopyReviewSchema>, 'unsupportedAssertions'> & {
  /** Absent on a review produced before this field existed, or by a caller that has no evidence list. */
  unsupportedAssertions?: string[];
};

/**
 * The bar. 7 is the band where copy stops being "competent but generic" and
 * starts being specific to this product and reader — which is exactly the line
 * the founder-facing quality goal draws.
 */
export const AD_COPY_HOOK_SCORE_BAR = 7;

export interface ReviewAdCopyArgs {
  /** The finished copy, rendered as the reader would encounter it. */
  renderedCopy: string;
  /** Which placement's conventions the reviewer should judge against. */
  platformLabel: string;
  /** What the ad is selling, so the reviewer can test for genericness. */
  product: string;
  /**
   * The citable evidence the copy was written from (verbatim source and brand
   * lines). When present the reviewer also checks every factual assertion in
   * the copy against it. Absent, the check is skipped and never faked.
   */
  evidence?: readonly string[];
  abortSignal?: AbortSignal;
  /**
   * How long the judge thinks before scoring. Omitted, the model's own default
   * applies (the chat tool's behaviour). The grounded engine passes 'low': the
   * verdict is a bounded rubric over a short prompt, and default thinking made
   * it the slowest single call in the interactive drafters.
   */
  thinkingLevel?: 'low' | 'medium' | 'high';
}

/** Returns null when the review itself failed — never a fabricated pass. */
export async function reviewAdCopy(args: ReviewAdCopyArgs): Promise<AdCopyReview | null> {
  const { renderedCopy, platformLabel, product, evidence, abortSignal, thinkingLevel } = args;
  const evidenceLines = (evidence ?? []).filter((line) => line.trim().length > 0);

  const system = [
    'You are a demanding paid-media creative director reviewing finished ad copy before it reaches a client.',
    'You did not write this copy and you have no attachment to it. Your job is to find what is weak, not to be encouraging.',
    'Score honestly against the bands you are given. Most copy is a 5 or 6; say so when it is.',
  ].join(' ');

  const prompt = [
    `Placement: ${platformLabel}`,
    `Product or offer: ${product}`,
    '',
    'The copy:',
    renderedCopy,
    '',
    'Score the strongest opening line 1–10 using these bands exactly:',
    ...HOOK_SCORE_BANDS,
    '',
    'Judge the hook on these five criteria:',
    ...HOOK_QUALITY_CRITERIA.map((criterion) => `- ${criterion}`),
    '',
    'Then answer the decisive question: could a direct competitor run this copy unchanged, swapping only the product name? If yes, it is generic, regardless of how polished it reads.',
    '',
    'ALSO CHECK — name these in `failsBecause` when present, even if the hook itself scores well:',
    ...AD_COPY_ADDITIONAL_JUDGE_CHECKS.map((check) => `- ${check}`),
    '',
    ...(evidenceLines.length > 0
      ? [
          'EVIDENCE CHECK — the only facts this copy may assert are stated in these lines:',
          ...evidenceLines.map((line) => `- ${line}`),
          'Read the copy sentence by sentence. Put in `unsupportedAssertions` (quoted) every sentence or clause that asserts a fact these lines do not state: a mechanism ("X inflates Y", "audit sequencing impacts valuation"), a claim about what a rule requires or causes, an account of what the reader\'s own situation is, or a claim that the advertiser "aggressively" or "always" does something. Also list: a product benefit or performance result the lines do not state (how it feels, grips, holds, lasts or performs, what it stops or prevents, what the reader will be able to do), any availability, stock, shipping, delivery, price advantage, "free" or "no cost", timing, season or place the lines do not state, and a narrower or different audience than the lines name. A feature the lines state may be repeated; turning that feature into a result ("grippy outsole" into "never slip") is not supported. Do NOT list: a figure or fact stated in the lines above, the advertiser\'s own description of itself that appears in them, or the closing offer or ask. A sentence that restates or narrows an evidence line is supported; a sentence that adds a cause, a consequence or a mechanism the lines do not contain is not.',
          '',
        ]
      : ['No evidence list was supplied: return `unsupportedAssertions` as an empty list.', '']),
    'In `failsBecause`, give concrete rewrites-worth-of-direction — name the field and say what should replace it. Do not list praise, and do not pad the list. Return an empty list only when the copy genuinely needs no rewrite.',
  ].join('\n');

  try {
    const result = await generateText({
      model: selectAdCopyTextModel(),
      system,
      prompt,
      output: Output.object({ schema: AdCopyReviewSchema }),
      ...(thinkingLevel
        ? { providerOptions: { google: { thinkingConfig: { thinkingLevel } } } }
        : {}),
      // Same bound as `repairAdCopy`, for the same reason: this critique pass
      // is one hop in a chain of up to ~10 sequential PRO-tier calls sharing a
      // single 150s outer tool timeout, and it can run TWICE (once per producer
      // pass). An unbounded review can consume the whole budget on its own.
      //
      // Safe by construction — the catch below already treats a failure as
      // "not reviewed" and ships the copy with that stated rather than
      // inventing a score, so a timeout degrades to the existing honest path.
      abortSignal: abortSignal
        ? AbortSignal.any([abortSignal, AbortSignal.timeout(25_000)])
        : AbortSignal.timeout(25_000),
    });
    return (result.output as AdCopyReview) ?? null;
  } catch (err) {
    // A failed review must never be reported as a passed review. The caller
    // treats null as "not reviewed" and ships the copy with that stated,
    // rather than inventing a score to fill the field.
    console.error('[reviewAdCopy] failed', {
      platformLabel,
      err: err instanceof Error ? { name: err.name, message: err.message } : err,
    });
    return null;
  }
}

/** True when the review found nothing worth another pass. */
export function reviewPasses(review: AdCopyReview | null): boolean {
  if (!review) return true;
  return (
    review.hookScore >= AD_COPY_HOOK_SCORE_BAR &&
    !review.isGeneric &&
    (review.unsupportedAssertions?.length ?? 0) === 0
  );
}
